/**
 * Secure Logger - Environment-based logging
 * Only logs in development, strips sensitive data in production
 */

const isDevelopment = process.env.NODE_ENV !== 'production';

// Patterns to detect and redact sensitive data
const SENSITIVE_PATTERNS = [
  /token[s]?["']?\s*[:=]\s*["']?[A-Za-z0-9._-]+/gi,
  /password["']?\s*[:=]\s*["']?[^\s,}]+/gi,
  /authorization["']?\s*[:=]\s*["']?Bearer\s+[A-Za-z0-9._-]+/gi,
  /session[_-]?id["']?\s*[:=]\s*["']?[A-Za-z0-9._-]+/gi,
  /cookie["']?\s*[:=]\s*["']?[^\s,}]+/gi,
  /api[_-]?key["']?\s*[:=]\s*["']?[A-Za-z0-9._-]+/gi,
  /secret["']?\s*[:=]\s*["']?[^\s,}]+/gi,
];

/**
 * Redact sensitive information from log arguments
 */
const redactSensitive = (args) => {
  return args.map(arg => {
    if (typeof arg === 'string') {
      let redacted = arg;
      SENSITIVE_PATTERNS.forEach(pattern => {
        redacted = redacted.replace(pattern, '[REDACTED]');
      });
      return redacted;
    }
    if (typeof arg === 'object' && arg !== null) {
      try {
        let str = JSON.stringify(arg);
        SENSITIVE_PATTERNS.forEach(pattern => {
          str = str.replace(pattern, '[REDACTED]');
        });
        return JSON.parse(str);
      } catch {
        return '[Object - could not stringify]';
      }
    }
    return arg;
  });
};

const logger = {
  /**
   * Debug logging - only in development
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug('[DEBUG]', ...redactSensitive(args));
    }
  },

  /**
   * Info logging - only in development
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info('[INFO]', ...redactSensitive(args));
    }
  },

  /**
   * Warning logging - always logged but redacted in production
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args);
    } else {
      console.warn('[WARN]', ...redactSensitive(args));
    }
  },

  /**
   * Error logging - always logged but redacted in production
   */
  error: (...args) => {
    if (isDevelopment) {
      console.error('[ERROR]', ...args);
    } else {
      console.error('[ERROR]', ...redactSensitive(args));
    }
  },

  /**
   * Security event logging - for audit purposes
   */
  security: (event, details = {}) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      ...details,
    };
    if (isDevelopment) {
      console.info('[SECURITY]', logEntry);
    }
    // In production, could send to security monitoring service
  },
};

export default logger;
